// PrimeReact MultiSelect: onChange(e) => e.value (array of selected values, typed as any).
// No existing usages in apps/web/src; shape confirmed from PrimeReact docs and type definitions:
//   - apps/web/node_modules/primereact/multiselect/multiselect.d.ts:533 — inputId?: string
// Event shape mirrors Dropdown: SelectMultipleEvent extends FormEvent<T[]> so e.value is T[].
// PrimeReact MultiSelect uses `inputId` to set the id on the inner focusable element.

import { useId, type JSX } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { MultiSelect } from 'primereact/multiselect'

interface MultiSelectOption {
  label: string
  value: string | number
}

interface Props {
  name: string
  label: string
  options: MultiSelectOption[]
  helpText?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  filter?: boolean
  display?: 'comma' | 'chip'
  maxSelectedLabels?: number
}

export function FormMultiSelect({
  name,
  label,
  options,
  helpText,
  placeholder,
  required,
  disabled,
  filter,
  display,
  maxSelectedLabels,
}: Props): JSX.Element {
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const error = errors[name]
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined
  const id = useId()
  const errorId = `${id}-error`
  const helpId = `${id}-help`
  const describedBy =
    [helpText && helpId, errorMessage && errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1 mb-3">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
        {required && <span aria-hidden> *</span>}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <MultiSelect
            inputId={id}
            value={field.value as unknown[]}
            onChange={(e) => field.onChange(e.value)}
            onBlur={field.onBlur}
            options={options}
            {...(placeholder !== undefined ? { placeholder } : {})}
            {...(disabled !== undefined ? { disabled } : {})}
            {...(filter !== undefined ? { filter } : {})}
            {...(display !== undefined ? { display } : {})}
            {...(maxSelectedLabels !== undefined ? { maxSelectedLabels } : {})}
            aria-invalid={error ? 'true' : 'false'}
            {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
          />
        )}
      />
      {helpText && (
        <small id={helpId} className="text-gray-500">
          {helpText}
        </small>
      )}
      {errorMessage && (
        <small id={errorId} role="alert" className="text-red-600">
          {errorMessage}
        </small>
      )}
    </div>
  )
}
